import React, { Fragment, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useInfiniteQuery, useQuery } from "react-query"
import { useParams } from "react-router-dom"

import { Grid } from "@mui/material"
import type { AxiosError, AxiosResponse } from "axios"

import { KonanPageHeader } from "../components/core/KonanPageHeader"
import { KonanSubHeader } from "../components/core/KonanSubHeader"
import { KonanEmptyState } from "../components/utility/KonanEmptyState"
import { TemplateCard, TemplateLoadingCard } from "../features/MarketPlace"
import type { CustomScriptTemplate } from "../features/MarketPlace/Interfaces"
import { KonanAPI } from "../services/KonanAPI"
import type { PaginatedScriptGroupListList } from "../types/generated/api/PaginatedScriptGroupListList"
import type { PaginatedScriptTemplateList } from "../types/generated/api/PaginatedScriptTemplateList"
import type { ScriptTemplate } from "../types/generated/api/ScriptTemplate"
import { extractPageFromBackEndPaginationLink } from "../utils/genericHelpers"

/**
 * A component that manages the display of scripts using an infinite scroll implementation.
 * It allows the user to filter, sort, and search through scripts. The component provides
 * interactions to create new scripts or use existing templates through dialog components.
 *
 * The component handles various states for loading and fetching scripts, as well as dialog
 * states for creating and managing scripts. It fetches scripts dynamically as the user scrolls,
 * making use of the `useInfiniteQuery` from react-query for data fetching and pagination.
 *
 * @returns {React.ReactElement} The Scripts component that includes the search, filter, sorting UI,
 * and an infinite scrolling list of ScriptCard components. It dynamically opens dialogs for script
 * creation and template usage based on user interactions.
 */
export function MarketPlace(): React.ReactElement {
  const { id: projectId } = useParams<{ id: string }>()
  const { t } = useTranslation("marketplace")

  // Configure intersection observer for infinite scroll functionality
  // Only triggers next page fetch when last item is in view and more data is available
  const { ref: lastScriptRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasNextPage && !isTemplatesLoading && !isFetchingNextPage && fetchNextPage()
    },
  })

  // SSF states
  const [searchValue, setSearchValue] = useState<string>("")

  // Workaround till backend return the isUsed with templates
  // fetch scripts
  const { data: scriptsData, isLoading: isScriptsDataLoading } = useQuery<
    AxiosResponse<PaginatedScriptGroupListList>,
    AxiosError
  >(["scripts", projectId], () => KonanAPI.fetchScripts(projectId as string, 1, 200, undefined, true), {
    enabled: !!projectId,
  })

  // fetch ready templates
  const {
    isLoading: isTemplatesLoading,
    data: templates,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedScriptTemplateList>, AxiosError, ScriptTemplate>(
    ["ready-templates", projectId, searchValue],
    ({ pageParam = 1 }) =>
      KonanAPI.fetchReadyTemplates({
        projectId: projectId as string,
        page: pageParam,
        page_size: 20,
        search: searchValue as string,
      }),
    {
      keepPreviousData: true,
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },

      enabled: !!projectId,
      refetchOnMount: true,
      select: ({ pageParams, pages }) => ({
        pageParams: pageParams,
        pages: pages.reduce(
          (accumulator, page) => accumulator.concat(page.data.results as Array<ScriptTemplate>),
          [] as Array<ScriptTemplate>,
        ),
      }),
    },
  )

  // extracting the results after infinite-Query
  const adjustedTemplates = useMemo(() => {
    if (isTemplatesLoading || isScriptsDataLoading) return []

    const allTemplates = templates?.pages as CustomScriptTemplate[]

    allTemplates?.forEach((template) => {
      const script = scriptsData?.data?.results?.find((script) => script?.template === template?.uuid)
      template.isInUse = script ? true : false
      template.script_uuid = script?.uuid
    })

    return allTemplates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates?.pages, scriptsData?.data?.results])

  return (
    <Fragment>
      <Grid container>
        <KonanPageHeader title={t("pageTitle")} />
        {isTemplatesLoading ||
        (!isTemplatesLoading && ((templates && templates.pages?.length > 0) || !!searchValue)) ? (
          <Fragment>
            <Grid item container my={2}>
              <KonanSubHeader
                searchValue={searchValue}
                isDebouncedInput
                setSearchValue={setSearchValue}
                containerType="Marketplace"
                useMenuFilter
              />
            </Grid>

            <Grid item xs={12}>
              {isTemplatesLoading || isScriptsDataLoading ? (
                <Grid container spacing={2}>
                  {[1, 2, 3].map((item: number) => (
                    <Grid item xs={12} md={6} lg={4} key={item}>
                      <TemplateLoadingCard />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Grid container spacing={2} pb={1}>
                  {adjustedTemplates && adjustedTemplates.length > 0 ? (
                    <Fragment>
                      {adjustedTemplates?.map((template, index) => (
                        <Grid
                          item
                          xs={12}
                          md={6}
                          lg={4}
                          key={template?.uuid}
                          ref={hasNextPage && index === adjustedTemplates.length - 1 ? lastScriptRef : undefined}
                        >
                          <TemplateCard template={template} />
                        </Grid>
                      ))}
                      {hasNextPage &&
                        [1, 2, 3].map((item: number) => (
                          <Grid item xs={12} md={6} lg={4} key={item}>
                            <TemplateLoadingCard />
                          </Grid>
                        ))}
                    </Fragment>
                  ) : !searchValue ? (
                    <KonanEmptyState
                      title={t("emptyState.noResultsForSearch", { searchValue })}
                      subTitle={t("emptyState.adjustSearchValue")}
                    />
                  ) : (
                    <KonanEmptyState
                      title={t("emptyState.noResultsFound")}
                      subTitle={t("emptyState.adjustSearchAndFilter")}
                    />
                  )}
                </Grid>
              )}
            </Grid>
          </Fragment>
        ) : (
          <Grid minHeight={"50vh"} container height="100%">
            <KonanEmptyState title={t("emptyState.noTemplatesYet")} subTitle={t("emptyState.createFirstIntegration")} />
          </Grid>
        )}
      </Grid>
    </Fragment>
  )
}
